import { BlockEmbed } from 'quill/blots/block';
import type Quill from 'quill/core';
import { Range } from 'quill/core/selection';
import type { Blot } from './types';
import isSelectableInlineEmbed from './utils/isSelectableInlineEmbed';

const positionMatches = (
  container: Node,
  offset: number,
  criteria: (node?: Node) => boolean,
) => {
  const isMatching = criteria(container);
  if (isMatching) {
    return isMatching;
  }
  try {
    return criteria(container.childNodes[offset]) || null;
  } catch (err) {
    // Sometimes we don't have permission to access child nodes.
    // For example, when the container is `iframe` in cross-domain case.
    return null;
  }
};

const patchMethod = <O extends Quill['selection'], K extends keyof O>(
  obj: O,
  methodName: K,
  impl: (origin: O[K]) => O[K],
) => {
  const origin = obj[methodName];
  if (typeof origin !== 'function') {
    return;
  }
  Object.assign(obj, { [methodName]: impl(origin.bind(obj)) });
};

const patchQuill = (quill: Quill, embedSelectionContainer: Node | null) => {
  // Store the "selected" blot when the browser selection is
  // in embed selection container.
  let embedSelectionBlot: Blot | null = null;

  patchMethod(quill.selection, 'rangeToNative', () => (range) => {
    const indexes =
      'collapsed' in range && range.collapsed
        ? [range.index]
        : [range.index, range.index + range.length];
    const scrollLength = quill.scroll.length();

    const [startBlot, startBlotOffset] = quill.scroll.leaf(
      Math.min(scrollLength - 1, indexes[0] as number),
    );

    if (
      (startBlot instanceof BlockEmbed && range.length <= startBlot.length()) ||
      (isSelectableInlineEmbed(startBlot) &&
        startBlotOffset === 0 &&
        range.length === 1)
    ) {
      embedSelectionBlot = startBlot;
      return [startBlot.domNode, 0, startBlot.domNode, 0];
    }

    const [endBlot, endBlotOffset] = quill.scroll.leaf(
      Math.min(scrollLength - 1, indexes[1] as number),
    );

    if (
      isSelectableInlineEmbed(endBlot) &&
      endBlotOffset === 1 &&
      range.length === 1
    ) {
      embedSelectionBlot = endBlot;
      return [endBlot.domNode, 0, endBlot.domNode, 0];
    }

    return [
      ...(startBlot?.position(startBlotOffset, false) ?? [null, 0]),
      ...(endBlot?.position(endBlotOffset, true) ?? [null, 0]),
    ];
  });

  patchMethod(quill.selection, 'normalizedToRange', (origin) => (range) => {
    const blot = quill.scroll.find(range.start.node);
    if (
      blot instanceof BlockEmbed ||
      (isSelectableInlineEmbed(blot) && range.native.collapsed)
    ) {
      return new Range(blot.offset(quill.scroll), blot.length());
    }
    return origin(range);
  });

  patchMethod(quill.selection, 'normalizeNative', (origin) => (nativeRange) => {
    let selectedEmbedBlot = null;

    const isSelectionInsideEmbedSelectionContainer =
      nativeRange.collapsed &&
      positionMatches(
        nativeRange.startContainer,
        nativeRange.startOffset,
        (node) => node === embedSelectionContainer,
      );

    if (isSelectionInsideEmbedSelectionContainer) {
      if (embedSelectionBlot?.domNode.isConnected) {
        selectedEmbedBlot = embedSelectionBlot;
      }
    } else {
      // If the selection is inside an embed blot.
      // For example, when editing the caption of an image blot, the selection
      // is in the caption input.
      const startBlot = quill.scroll.find(nativeRange.startContainer, true);
      if (
        (startBlot instanceof BlockEmbed &&
          startBlot.domNode.contains(nativeRange.endContainer)) ||
        (isSelectableInlineEmbed(startBlot) &&
          (startBlot.contentNode?.contains(nativeRange.endContainer) ?? false))
      ) {
        selectedEmbedBlot = startBlot;
      }
    }

    if (selectedEmbedBlot) {
      // If an embed blot is selected, the blot's `domNode` will be returned.
      return {
        start: {
          node: selectedEmbedBlot.domNode,
          offset: 0,
        },
        end: {
          node: selectedEmbedBlot.domNode,
          offset: 0,
        },
        native: {
          ...nativeRange,
          startContainer: selectedEmbedBlot.domNode,
          startOffset: 0,
          endContainer: selectedEmbedBlot.domNode,
          endOffset: 0,
          collapsed: true,
        },
      };
    }

    return origin(nativeRange);
  });

  patchMethod(
    quill.selection,
    'setNativeRange',
    () =>
      (
        startNode,
        startOffset,
        endNode = startNode ?? undefined,
        endOffset = startOffset,
        force = false,
      ) => {
        if (
          startNode != null &&
          (quill.selection.root.parentNode == null ||
            startNode.parentNode == null ||
            endNode?.parentNode == null)
        ) {
          return;
        }
        const selection = document.getSelection();
        if (selection == null) return;
        if (startNode != null) {
          if (!quill.selection.hasFocus()) {
            quill.selection.root.focus({ preventScroll: true });
          }
          const { native } = quill.selection.getNativeRange() || {};
          if (
            native == null ||
            force ||
            startNode !== native.startContainer ||
            startOffset !== native.startOffset ||
            endNode !== native.endContainer ||
            endOffset !== native.endOffset
          ) {
            /* eslint-disable no-param-reassign */
            if ((startNode as Element).tagName === 'BR') {
              startOffset = Array.from(
                startNode.parentNode?.childNodes ?? [],
              ).indexOf(startNode as ChildNode);
              startNode = startNode.parentNode;
            }
            if (endNode && (endNode as Element).tagName === 'BR') {
              endOffset = Array.from(
                endNode.parentNode?.childNodes ?? [],
              ).indexOf(endNode as ChildNode);
              endNode = endNode.parentNode ?? undefined;
            }
            // Most code in this method is copied from the original one.
            // All we need is to move the selection to the embed selection container
            // if the target is a block embed.
            const blot = quill.scroll.find(startNode);
            if (
              blot instanceof BlockEmbed ||
              (isSelectableInlineEmbed(blot) && startNode === endNode)
            ) {
              startNode = embedSelectionContainer;
              startOffset = 0;
              endNode = embedSelectionContainer ?? undefined;
              endOffset = 0;
            }
            const range = document.createRange();
            if (startNode && startOffset != null) {
              range.setStart(startNode, startOffset);
            }
            if (endNode && endOffset != null) {
              range.setEnd(endNode, endOffset);
            }
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else {
          selection.removeAllRanges();
          quill.selection.root.blur();
        }
      },
  );
};

export default patchQuill;
